/**
 * BezierMapEdge — curved connection between two planets.
 *
 * Always draws center-to-center (dots paint on top, visually swallowing the
 * line ends). The curve is a Catmull-Rom spline through all stored waypoints:
 * click the line to insert a waypoint, drag any waypoint handle to reshape it,
 * right-click a handle to delete it. No waypoints → straight line.
 */

import { useState } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  useInternalNode,
  useReactFlow,
  type EdgeProps,
} from '@xyflow/react';

export interface BezierMapEdgeData extends Record<string, unknown> {
  controlPoints: Array<{ x: number; y: number }>;
  canvasWidth: number;
  canvasHeight: number;
  readOnly: boolean;
  onControlPointsChange: (controlPoints: Array<{ x: number; y: number }>) => void;
}

// First-frame fallback until React Flow measures the dot. Matches 3rem (48px) dot.
const DOT_RADIUS = 24;

interface Pt { x: number; y: number }

const center = (node: ReturnType<typeof useInternalNode>): Pt => {
  const p = node!.internals.positionAbsolute;
  return {
    x: p.x + (node!.measured.width  ?? DOT_RADIUS * 2) / 2,
    y: p.y + (node!.measured.height ?? DOT_RADIUS * 2) / 2,
  };
};

/**
 * Convert an ordered array of points to a Catmull-Rom spline SVG path string.
 * Phantom endpoints are added by mirroring the nearest interior neighbours so
 * the curve exits each endpoint tangentially along the first/last segment.
 */
function catmullRomPath(pts: Pt[]): string {
  if (pts.length < 2) return '';

  const ext: Pt[] = [
    { x: 2 * pts[0].x - pts[1].x,                           y: 2 * pts[0].y - pts[1].y },
    ...pts,
    { x: 2 * pts[pts.length - 1].x - pts[pts.length - 2].x, y: 2 * pts[pts.length - 1].y - pts[pts.length - 2].y },
  ];

  let d = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < ext.length - 2; i++) {
    const p0 = ext[i - 1], p1 = ext[i], p2 = ext[i + 1], p3 = ext[i + 2];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
}

/** Perpendicular distance from p to the finite segment a→b. */
function segmentDist(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/**
 * Returns the index in allPts after which a new waypoint at `click` should be
 * inserted — i.e. the index of the nearest segment's start point.
 */
function insertIndex(allPts: Pt[], click: Pt): number {
  let best = 0, bestDist = Infinity;
  for (let i = 0; i < allPts.length - 1; i++) {
    const d = segmentDist(click, allPts[i], allPts[i + 1]);
    if (d < bestDist) { bestDist = d; best = i; }
  }
  return best;
}

export function BezierMapEdge({ id, data, style, source, target }: EdgeProps) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const { screenToFlowPosition } = useReactFlow();
  const [dragging, setDragging] = useState<{ index: number; pos: Pt } | null>(null);

  if (!sourceNode || !targetNode) return null;

  const ed = (data ?? {}) as Partial<BezierMapEdgeData>;
  const canvasWidth   = ed.canvasWidth  ?? 1;
  const canvasHeight  = ed.canvasHeight ?? 1;
  const readOnly      = ed.readOnly     ?? true;
  const controlPoints = ed.controlPoints ?? [];

  const c0 = center(sourceNode);
  const c1 = center(targetNode);

  // Waypoints in flow-space, with the actively dragged one swapped in live.
  const flowWaypoints: Pt[] = controlPoints.map((cp, i) =>
    dragging?.index === i
      ? dragging.pos
      : { x: cp.x * canvasWidth, y: cp.y * canvasHeight }
  );

  const allPts   = [c0, ...flowWaypoints, c1];
  const edgePath = catmullRomPath(allPts);

  return (
    <>
      <BaseEdge id={id} path={edgePath} style={style} />

      {!readOnly && (
        <>
          {/* Wide transparent hit-area so clicks land on the line easily */}
          <path
            d={edgePath}
            stroke="transparent"
            strokeWidth={14}
            fill="none"
            style={{ pointerEvents: 'stroke', cursor: 'crosshair' }}
            onClick={(e) => {
              e.stopPropagation();
              const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
              const idx     = insertIndex(allPts, flowPos);
              const updated = [...controlPoints];
              updated.splice(idx, 0, { x: flowPos.x / canvasWidth, y: flowPos.y / canvasHeight });
              ed.onControlPointsChange?.(updated);
            }}
          />

          <EdgeLabelRenderer>
            {flowWaypoints.map((wp, i) => (
              <div
                key={i}
                className="trl-map-builder__edge-handle nodrag nopan"
                style={{ transform: `translate(-50%, -50%) translate(${wp.x}px, ${wp.y}px)` }}
                title="Drag to reshape · right-click to remove"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  (e.target as HTMLElement).setPointerCapture(e.pointerId);
                  setDragging({ index: i, pos: screenToFlowPosition({ x: e.clientX, y: e.clientY }) });
                }}
                onPointerMove={(e) => {
                  if (dragging?.index !== i) return;
                  setDragging({ index: i, pos: screenToFlowPosition({ x: e.clientX, y: e.clientY }) });
                }}
                onPointerUp={(e) => {
                  const cur = dragging;
                  if (!cur || cur.index !== i) return;
                  (e.target as HTMLElement).releasePointerCapture(e.pointerId);
                  const updated = controlPoints.map((cp, j) =>
                    j === i ? { x: cur.pos.x / canvasWidth, y: cur.pos.y / canvasHeight } : cp
                  );
                  ed.onControlPointsChange?.(updated);
                  setDragging(null);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  ed.onControlPointsChange?.(controlPoints.filter((_, j) => j !== i));
                }}
              />
            ))}
          </EdgeLabelRenderer>
        </>
      )}
    </>
  );
}
